import { CopyIcon } from "@xola/icons";
import { Button, flash } from "@xola/ui-kit";
import { now } from "common";
import dayjs from "dayjs";
import { startCase } from "lodash";
import React, { useEffect } from "react";
import { EmvDevice, FeatureRole, Seller, User } from "types";
import UAParser from "ua-parser-js";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useCollection } from "../../hooks/useCollection";
import { hasFeature } from "../../models/Seller";
import { isXolaAdmin } from "../../models/User";
import { log } from "../../utils/log";
import { SecondaryButton } from "../Common/SecondaryButton";
import { Devices } from "../Settings/Hardware/PaymentTerminalForm";
import { Adblock } from "./Adblock";
import { BrowserInfo } from "./BrowserInfo";
import { CurrentTime } from "./CurrentTime";
import { CustomerSupportInformation } from "./CustomerSupportInformation";
import { OutdatedTimezoneData } from "./OutdatedTimezoneData";
import { ReleaseVersion } from "./ReleaseVersion";
import { SupportItem } from "./SupportItem";
import { SellerInfo } from "./UserInfo";

const { userAgent } = navigator;
const ua = new UAParser(userAgent);

export const SystemInformation = () => {
    const user = useUser();
    const seller = useSeller();
    const { screen, innerWidth, innerHeight, devicePixelRatio } = window;
    const logParams = useLogParams();

    const [browser, device, os] = [ua.getBrowser(), ua.getDevice(), getOS()];

    const hasEMV = hasFeature(seller, FeatureRole.EMV);
    const { data: devices } = useCollection<EmvDevice>(
        ["users", seller.id, "preferences", "stripeTerminal", "paymentDevices"],
        null,
        { enabled: hasEMV },
    );

    const desktopResolution = `${screen.availWidth} x ${screen.availHeight}`;
    const nativeResolution = `${screen.availWidth * devicePixelRatio} x ${screen.availHeight * devicePixelRatio}`;

    useEffect(() => {
        log("Support page opened", logParams, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCopy = () => {
        const content = copyInfo(seller, user, ua);
        navigator.clipboard.writeText(content.replace(/^ {8,}/gm, "").trim());
        flash.show({
            text: (
                <pre className="no-translate font-sans text-sm">
                    <span className="font-semibold">Copied:</span>
                    <br />
                    {content}
                </pre>
            ),
            icon: CopyIcon,
        });
    };

    const now = dayjs().format("ll hh:mm A Z");
    const timeInTimezone = dayjs().tz().format("ll hh:mm A Z");

    return (
        <div className="flex flex-col space-x-8 space-y-2 xl:flex-row xl:space-y-0">
            <div className="w-1/2 space-y-4">
                <h2>
                    System Information
                    <SecondaryButton size="tiny" className="ml-2" onClick={handleCopy}>
                        <CopyIcon />
                    </SecondaryButton>
                </h2>

                <div className="ui-support-details space-y-4">
                    <ReleaseVersion />
                    <div>
                        <h4 className="font-semibold">User</h4>
                        <div className="flex flex-col space-y-1">
                            <SellerInfo />
                            <SupportItem
                                name="Time"
                                copyText={`Time: ${now} and ${timeInTimezone !== now && timeInTimezone}`}
                                value={<CurrentTime />}
                            />
                        </div>
                    </div>
                    <div>
                        <h4 className="font-semibold">Resolution</h4>
                        <SupportItem name="Desktop" value={desktopResolution} />
                        {desktopResolution !== nativeResolution && (
                            <SupportItem name="Native" value={nativeResolution} />
                        )}
                    </div>
                    <div>
                        <h4 className="font-semibold">Browser</h4>
                        <SupportItem name="Name" copyText={navigator.userAgent} value={<BrowserInfo />} />
                        <SupportItem name="Resolution" value={`${innerWidth} x ${innerHeight}`} />
                    </div>
                    <div>
                        <SupportItem name="Operating System" value={`${os.name} ${os.version}`} />
                    </div>
                    {device.vendor && (
                        <div>
                            <h4 className="font-semibold">Machine</h4>
                            <SupportItem name="Vendor" value={`${device.vendor}`} />
                            <SupportItem name="Model" value={`${device.model}`} />
                            <SupportItem name="Type" value={`${startCase(device.type)}`} />
                        </div>
                    )}

                    {hasEMV && devices.length > 0 && (
                        <div>
                            <h4 className="font-semibold">Payment Devices</h4>
                            <div className="!mt-2 space-y-2">
                                <Devices devices={devices} canEdit={false} />
                            </div>
                        </div>
                    )}

                    <Adblock />
                    <OutdatedTimezoneData />

                    <div className="no-translate font-mono text-sm text-gray">
                        {userAgent} on {Date.now()}
                    </div>
                </div>

                <div>
                    <Button color="success" icon={<CopyIcon />} onClick={handleCopy}>
                        Copy Information
                    </Button>
                </div>
            </div>
            <div className="w-1/2 space-y-4">
                <CustomerSupportInformation browser={browser} os={os} />
            </div>
        </div>
    );
};

const getOS = () => {
    let os: UAParser.IOS | string = ua.getOS();

    try {
        (navigator as any).userAgentData.getHighEntropyValues(["platformVersion"]).then((ua: any) => {
            if ((navigator as any).userAgentData.platform === "Windows") {
                const majorPlatformVersion = Number.parseInt(ua.platformVersion.split(".")[0], 10);
                if (majorPlatformVersion >= 13) {
                    os = "Windows 11 or later";
                } else if (majorPlatformVersion > 0) {
                    os = "Windows 10";
                }
            }
        });
    } catch {}

    return os;
};

export const basicInfo = (seller: Seller, user: User) => {
    const isSeller = seller.id === user.id;
    return `Seller: ${seller.name} (${seller.id})
${isSeller ? "Logged in as owner" : `Logged in as: ${user.name} (${user.id})`}\n`;
};

export const copyInfo = (seller: Seller, user: User, ua: any, admin = false) => {
    const [browser, device, os] = [ua.getBrowser(), ua.getDevice(), ua.getOS()];

    const desktopResolution = `${screen.availWidth} x ${screen.availHeight}`;
    const environment = process.env.ENVIRONMENT;

    const osInfoForAdmin = `${os.name}, ${browser.name}`;
    const osInfoForUser = `\n* OS: ${os.name} ${os.version}
* Browser: ${browser.name} ${browser.version}
* Resolution: ${desktopResolution.trim()}${
        device.vendor ? `\n* Device: ${device.vendor} ${device.model} ${device.type ?? ""}` : ""
    }`;
    const osInfo = admin ? osInfoForAdmin : osInfoForUser;

    const localTime = dayjs().format("YYYY-MM-DD HH:mm:ss Z");
    const sellerTime = now().format("YYYY-MM-DD HH:mm:ss Z");
    let timeDisplay = `Time: ${sellerTime} (${seller.timezoneName})`;
    if (sellerTime !== localTime) {
        timeDisplay = `Seller Time: ${sellerTime} (${seller.timezoneName})\n`;
        timeDisplay += `User Time:   ${localTime} (${dayjs.tz.guess()})`;
    }

    let appInfo = `Version: ${process.env.VERSION_HASH} ${
        environment !== "production" && `Environment: ${startCase(environment)}`
    }`;
    if (environment === "development") {
        appInfo = `Environment: ${startCase(environment)}`;
    }

    return `${basicInfo(seller, user)}${osInfo}
App ${appInfo}
${timeDisplay}
`;
};

const useLogParams = () => {
    const user = useUser();
    const seller = useSeller();
    const { screen, innerWidth, innerHeight, devicePixelRatio } = window;

    const { userAgent } = navigator;
    const ua = new UAParser(userAgent);
    const device = ua.getDevice();

    const hasEMV = hasFeature(seller, FeatureRole.EMV);
    const { data: devices } = useCollection<EmvDevice>(
        ["users", seller.id, "preferences", "stripeTerminal", "paymentDevices"],
        null,
        { enabled: hasEMV },
    );

    const desktopResolution = `${screen.availWidth} x ${screen.availHeight}`;
    const nativeResolution = `${screen.availWidth * devicePixelRatio} x ${screen.availHeight * devicePixelRatio}`;

    // Log opening this page to SumoLogic. This will help us incase the user incorrectly copy/pastes the info
    // or any other problem. This acts as a backup to search as well
    const logParams: any = {
        date: dayjs().format(),
        isXolaAdmin: isXolaAdmin(user),
        other: {
            userAgent,
            browser: ua.getBrowser(),
            desktopResolution,
            nativeResolution,
            browserResolution: {
                innerWidth,
                innerHeight,
            },
        },
    };

    if (device.vendor) {
        logParams.other.device = device;
    }

    if (hasEMV && devices.length > 0) {
        logParams.other.emvDevices = devices;
    }

    return logParams;
};
